import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { NgxEchartsModule } from 'ngx-echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { ChartWrapperService, executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { buildConfigurableAxisLabel, formatTooltipValue, getTooltipLabel } from '../../utils/chart.utils';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    CapitalCasePipe,
  ],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrl: './quad-bubble-chart.component.scss',
})
export class QuadBubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private legendSeriesNames: string[] = [];
  private focusedQuadrantIndex: number | null = null;
  private renderedPoints: any[] = [];
  private lastBubbleInteractionAt = 0;
  private currentDetailRows: any[] = [];
  private currentMatchedDetailRowIndexes = new Set<number>();

  get resolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }

  private configService = inject(ConfigService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private globalFilterService = inject(GlobalFilterService);
  private wrapper = inject(ChartWrapperService).createInstance();
  private router = inject(Router);
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  private dataSub?: Subscription;
  protected chart!: echarts.ECharts;
  private lastChartData?: any;

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.data.widgetId);
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.chart,
      getSeriesNames: () => this.legendSeriesNames,
      getCategories: () => [],
    });
    if (chartDom) {
      this.chart = echarts.init(chartDom, 'dvm');
      this.loadAndRenderChart(initialFilters ?? undefined);
      window.addEventListener('resize', this.resizeChart);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.chart?.resize();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chart) return;
      const dom = document.getElementById(this.data.widgetId);
      if (!dom) return;
      this.chart.dispose();
      this.chart = echarts.init(dom, 'dvm');
      this.renderChart(this.lastChartData || this.resolveChartData(this.data));
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    if (!this.chart) return;

    const apiUrl = String(this.data?.apiUrl || '').trim();
    const method = String(this.data?.method || 'get').toLowerCase() as 'get' | 'post';

    if (apiUrl) {
      this.dataSub?.unsubscribe();
      const isLocalAsset = apiUrl.startsWith('assets') || apiUrl.startsWith('/assets');
      const sanitizedFilters = this.sanitizeUiOnlyFilters(filters);
      const request$ = isLocalAsset
        ? this.configService.getData(apiUrl)
        : this.configService.getConfig(apiUrl, this.data, sanitizedFilters, method);

      this.dataSub = request$.subscribe({
        next: (response: any) => this.renderChart(this.resolveChartData(response)),
        error: () => this.renderChart(this.resolveChartData(this.data)),
      });
      return;
    }

    this.renderChart(this.resolveChartData(this.data));
  }

  protected renderChart(chartData?: any): void {
    if (chartData) {
      this.lastChartData = chartData;
    }

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.max(1, Math.round(px * fs));

    const raw = chartData;
    this.currentDetailRows = this.resolveQuadrantDetailRows(chartData);
    this.currentMatchedDetailRowIndexes.clear();
    const symbolSize = this.data.symbolSize || 15;
    const xAxisCfg = this.data?.chartConfig?.xAxis || {};
    const yAxisCfg = this.data?.chartConfig?.yAxis || {};
    const xAxisLabelCfg = (buildConfigurableAxisLabel(xAxisCfg) as any) || {};
    const yAxisLabelCfg = (buildConfigurableAxisLabel(yAxisCfg) as any) || {};
    const axisNameStyle = {
      color: theme.chart?.textColor || theme.chart?.axisLabelColor || '#111827',
      fontSize: sc(14),
      fontWeight: 700,
      ...(this.data?.chartConfig?.axisNameTextStyle || {}),
    };
    const shouldApplyQuadrantFocus = this.data?.showSelectedQuadrantOnly === true;
    const focusedSelection = shouldApplyQuadrantFocus ? this.resolveFocusedQuadrantSelection() : null;

    const allPoints: any[] = [
      ...this.buildQuadrantPoints(raw?.q1 ?? [], 0),
      ...this.buildQuadrantPoints(raw?.q2 ?? [], 1),
      ...this.buildQuadrantPoints(raw?.q3 ?? [], 2),
      ...this.buildQuadrantPoints(raw?.q4 ?? [], 3),
    ].map((point, index, allPoints) => ({
      ...point,
      color: point?.color || this.buildBubbleColor(point, index, allPoints.length),
    }));

    let points: any[] = allPoints;
    let focusedQuadrantIndex = focusedSelection?.quadrantIndex ?? null;
    const hasExplicitSelectedQuadrant = focusedSelection?.hasExplicitQuadrantSelection === true;
    if (focusedSelection) {
      const selectedTypeNormalized = this.normalizeCategoryValue(focusedSelection.productPriceType);
      const matchedQuadrants = new Set<number>();
      points = points.filter((point) => {
        const pointType = point?.product_price_type
          ? String(point.product_price_type)
          : this.resolveQuadrantFilterValue(point?.quadrant, Number(point?.quadrantIndex ?? -1));
        const isMatch = this.normalizeCategoryValue(pointType) === selectedTypeNormalized;
        if (isMatch) {
          const idx = Number(point?.quadrantIndex);
          if (Number.isFinite(idx) && idx >= 0) {
            matchedQuadrants.add(idx);
          }
        }
        return isMatch;
      });

      if (!hasExplicitSelectedQuadrant && matchedQuadrants.size === 1) {
        focusedQuadrantIndex = Array.from(matchedQuadrants)[0];
      }
    }

    this.focusedQuadrantIndex = focusedQuadrantIndex;
    this.renderedPoints = points;

    const legendSeries = this.buildLegendSeries(points);
    this.legendSeriesNames = legendSeries.map((series) => series.name);
    const bubbleLabelKey = String(this.data?.bubbleLabelKey || this.data?.pointLabelKey || '').trim();
    const showBubbleLabels = Boolean(bubbleLabelKey) && this.data?.showBubbleLabels === true;
    const bubbleLabelPosition = String(this.data?.bubbleLabelPosition || 'top');
    const bubbleLabelColor = String(this.data?.bubbleLabelColor || theme.chart?.textColor || '#111827');
    const bubbleLabelFontSize = Number(this.data?.bubbleLabelFontSize) || sc(11);
    const bubbleLabelFontWeight = this.data?.bubbleLabelFontWeight ?? 600;
    const focusedBounds = shouldApplyQuadrantFocus && focusedQuadrantIndex !== null
      ? this.getQuadrantBounds(focusedQuadrantIndex)
      : null;
    const xMin = focusedBounds && this.shouldZoomToFocusedQuadrant()
      ? focusedBounds.minX
      : (this.data.xAxis?.min ?? 0);
    const xMax = focusedBounds && this.shouldZoomToFocusedQuadrant()
      ? focusedBounds.maxX
      : (this.data.xAxis?.max ?? 10);
    const yMin = focusedBounds && this.shouldZoomToFocusedQuadrant()
      ? focusedBounds.minY
      : (this.data.yAxis?.min ?? 0);
    const yMax = focusedBounds && this.shouldZoomToFocusedQuadrant()
      ? focusedBounds.maxY
      : (this.data.yAxis?.max ?? 10);

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = this.buildTooltipContext(params);
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const rawValue = this.resolveTooltipValue(d, field.key);
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          if (typeof tooltipCfg?.formatter === 'string' && tooltipCfg.formatter.trim()) {
            return (params: any) => {
              const d = this.buildTooltipContext(params);
              return this.interpolateTooltipTemplate(tooltipCfg.formatter, d);
            };
          }
          return (params: any) => {
            const d = params.data || {};
            const rawValue = d.value || [];
            const xAxisName = this.resolveTooltipAxisName(xAxisCfg?.name, 'x');
            const yAxisName = this.resolveTooltipAxisName(yAxisCfg?.name, 'y');
            return `<b>${xAxisName}:</b> ${this.formatCoordinateTooltipValue(d.x ?? rawValue[0])}<br/><b>${yAxisName}:</b> ${this.formatCoordinateTooltipValue(d.y ?? rawValue[1])}`;
          };
        })(),
      },
      legend: {
        show: !this.data?.hideLegend,
        type: 'scroll',
        orient: 'horizontal',
        left: 'center',
        top: sc(4),
        itemGap: sc(10),
        itemWidth: sc(14),
        itemHeight: sc(12),
        icon: 'circle',
        textStyle: {
          fontSize: sc(11),
          color: theme.chart?.textColor || theme.chart?.axisLabelColor || '#111827',
        },
        data: this.legendSeriesNames,
      },
      xAxis: {
        min: xMin,
        max: xMax,
        splitLine: { show: false },
        axisLabel: {
          fontSize: sc(10),
          hideOverlap: true,
          color: theme.chart?.axisLabelColor,
          ...xAxisLabelCfg,
          ...(xAxisCfg.axisLabel || {}),
        },
        name: xAxisCfg.name,
        nameLocation: xAxisCfg.nameLocation || 'middle',
        nameGap: xAxisCfg.nameGap ?? sc(28),
        nameTextStyle: {
          ...axisNameStyle,
          ...(xAxisCfg.nameTextStyle || {}),
        },
      },
      yAxis: {
        min: yMin,
        max: yMax,
        splitLine: { show: false },
        axisLabel: {
          fontSize: sc(10),
          hideOverlap: true,
          color: theme.chart?.axisLabelColor,
          ...yAxisLabelCfg,
          ...(yAxisCfg.axisLabel || {}),
        },
        name: yAxisCfg.name,
        nameLocation: yAxisCfg.nameLocation || 'middle',
        nameGap: yAxisCfg.nameGap ?? sc(36),
        nameRotate: yAxisCfg.nameRotate ?? 90,
        nameTextStyle: {
          ...axisNameStyle,
          ...(yAxisCfg.nameTextStyle || {}),
        },
      },
      grid: this.data?.chartConfig?.grid || { left: '8%', right: '3%', top: '16%', bottom: '12%', containLabel: true },
      series: legendSeries.map((series, index) => ({
        name: series.name,
        type: 'scatter',
        // Bubbles plotted at/near the axis max render with their fixed pixel
        // radius extending past the grid box (ECharts scatter defaults to
        // clip: false), which visually pokes them out past the quadrant
        // markArea rectangle since that rectangle stops exactly at the grid
        // edge. Clipping to the grid keeps every bubble flush inside its
        // quadrant instead of spilling onto the background outside it.
        clip: true,
        data: series.points,
        encode: { x: 0, y: 1, tooltip: [0, 1] },
        label: showBubbleLabels
          ? {
            show: true,
            position: bubbleLabelPosition as any,
            color: bubbleLabelColor,
            fontSize: bubbleLabelFontSize,
            fontWeight: bubbleLabelFontWeight as any,
            formatter: (params: any) => {
              const point = params?.data || {};
              const value = this.resolveTooltipValue(point, bubbleLabelKey);
              return value === undefined || value === null ? '' : String(value);
            },
          }
          : { show: false },
        symbolSize: (value: any) => {
          const bubbleSize = Array.isArray(value) ? value[3] : undefined;
          return this.resolveVisibleBubbleSize(bubbleSize, symbolSize);
        },
        itemStyle: {
          color: (params: any) => params?.data?.color || series.color,
          borderColor: 'transparent',
        },
        markArea: index === 0
          ? {
            silent: true,
            itemStyle: { opacity: 0.2 },
            data: this.getQuadrantAreas(this.focusedQuadrantIndex),
          }
          : undefined,
      })),
    };

    applyRoleVisibilityToOption(option, this.data);

    this.chart.setOption(option, true);
    this.setupBubbleClickEvent();
    this.setupQuadrantAreaClickEvent();
    setTimeout(() => this.updateQuadrantTitleGraphics(), 100);
  }

  protected sanitizeUiOnlyFilters(filters?: any): any {
    if (!filters || typeof filters !== 'object') return filters;
    const clone = {
      ...(filters || {}),
      requestParams: { ...(filters?.requestParams || {}) },
      queryParams: { ...(filters?.queryParams || {}) },
    };
    delete clone.requestParams?.selected_quadrant;
    delete clone.requestParams?.selected_quadrant_index;
    delete clone.requestParams?.price_increase;
    delete clone.requestParams?.uid;
    delete clone.queryParams?.selected_quadrant;
    delete clone.queryParams?.selected_quadrant_index;
    delete clone.queryParams?.price_increase;
    delete clone.queryParams?.uid;
    return clone;
  }

  protected updateQuadrantTitleGraphics(): void {
    const allQuads = (this.data?.quadrantBackgrounds || []).filter((q: any) => q.title);
    const focusedQuadrantCode =
      this.focusedQuadrantIndex === null || this.focusedQuadrantIndex < 0
        ? null
        : `Q${this.focusedQuadrantIndex + 1}`;
    const quads = (this.focusedQuadrantIndex === null || this.focusedQuadrantIndex < 0)
      ? allQuads
      : allQuads.filter((q: any) => this.extractQuadrantCodeFromTitle(q?.title) === focusedQuadrantCode);
    if (!quads.length || !this.chart) return;

    const xMin = this.data?.xAxis?.min ?? 0;
    const xMax = this.data?.xAxis?.max ?? 10;
    const xMid = (xMin + xMax) / 2;

    const graphics: any[] = quads.map((quad: any) => {
      const topY = Math.max(quad.yAxis ?? 0, quad.y2 ?? 0);
      const leftX = Math.min(quad.xAxis ?? 0, quad.x2 ?? 0);
      const rightX = Math.max(quad.xAxis ?? 0, quad.x2 ?? 0);
      const isRightQuad = leftX >= xMid;
      const anchorX = isRightQuad ? rightX : leftX;

      const px = this.chart.convertToPixel({ seriesIndex: 0 }, [anchorX, topY]) as number[];
      if (!px) return null;

      return {
        type: 'text',
        style: {
          text: quad.title,
          x: isRightQuad ? px[0] - 8 : px[0] + 8,
          y: px[1] + 8,
          textAlign: isRightQuad ? 'right' : 'left',
          textVerticalAlign: 'top',
          fill: '#111827',
          fontSize: 12,
          fontWeight: 'bold',
          lineHeight: 18,
        },
      };
    }).filter(Boolean);

    this.chart.setOption({ graphic: graphics }, false);
  }

  protected buildQuadrantPoints(points: any[], quadrantIndex: number): any[] {
    return points.map((point, index) => this.normalizeQuadrantPoint(point, quadrantIndex, index));
  }

  protected buildLegendSeries(points: any[]): Array<{ name: string; points: any[]; color: string }> {
    const grouped = new Map<string, any[]>();

    for (const point of points) {
      const legendName = String(point?.legend || point?.label || point?.name || 'Uncategorized').trim() || 'Uncategorized';
      const current = grouped.get(legendName) || [];
      current.push(point);
      grouped.set(legendName, current);
    }

    return Array.from(grouped.entries()).map(([name, groupedPoints]) => ({
      name,
      points: groupedPoints,
      color: String(groupedPoints?.[0]?.color || '#37A2DA'),
    }));
  }

  protected buildBubbleColor(point: any, index: number, total: number): string {
    const stableSeed = String(
      point?.key ??
      point?.customer_name ??
      point?.name ??
      point?.label ??
      `${index}`,
    );
    let hash = 0;
    for (let i = 0; i < stableSeed.length; i += 1) {
      hash = ((hash << 5) - hash) + stableSeed.charCodeAt(i);
      hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 68%, 52%)`;
  }

  protected resolveVisibleBubbleSize(rawBubbleSize: any, fallbackSize: number): number {
    const fallback = Number(fallbackSize) || 15;
    const numericSize = Number(rawBubbleSize);

    if (!Number.isFinite(numericSize) || numericSize <= 0) {
      return fallback;
    }

    if (numericSize < 1) {
      return Math.max(numericSize * fallback * 6, 10);
    }

    return Math.max(numericSize, 10);
  }

  protected normalizeQuadrantPoint(point: any, quadrantIndex: number, index: number): any {
    const quadrant = `Q${quadrantIndex + 1}`;
    const isArrayPoint = Array.isArray(point);
    const x = Number(isArrayPoint ? point[0] : point?.x ?? point?.x_axis ?? point?.value?.[0] ?? 0);
    const y = Number(isArrayPoint ? point[1] : point?.y ?? point?.y_axis ?? point?.value?.[1] ?? 0);
    const baseKey = String(
      point?.key ??
        point?.id ??
        point?.name ??
        point?.label ??
        `${quadrant.toLowerCase()}-${index + 1}`,
    );
    const key = `${baseKey}-${x}-${y}-${index}`;
    const label = String(point?.label ?? point?.name ?? point?.title ?? key);
    const legend = String(point?.legend ?? point?.seriesName ?? label);
    const size = Number(
      point?.size ??
        point?.symbolSize ??
        (isArrayPoint ? point?.[2] : point?.value?.[3]) ??
        this.data?.symbolSize ??
        15,
    );

    // Preserve additional data properties from original point (e.g., engine_model, customer_name)
    const extraProps: Record<string, any> = {};
    if (!isArrayPoint && typeof point === 'object') {
      const excludeKeys = new Set(['key', 'id', 'name', 'label', 'legend', 'seriesName', 'title', 'x', 'y', 'size', 'symbolSize', 'value']);
      Object.keys(point).forEach((k) => {
        if (!excludeKeys.has(k)) {
          extraProps[k] = point[k];
        }
      });
    }

    const matchedDetailRow = this.findMatchingDetailRow(
      {
        ...point,
        x,
        y,
        legend,
        label,
      },
      quadrantIndex,
    );
    const detailProps = this.extractPrimitivePointProps(matchedDetailRow);

    return {
      name: label,
      key,
      label,
      legend,
      quadrant,
      quadrantIndex,
      x,
      y,
      size,
      value: [x, y, quadrantIndex, size],
      ...extraProps,
      ...detailProps,
    };
  }

  protected resolveQuadrantDetailRows(chartData: any): any[] {
    if (Array.isArray(chartData?.__detailRows)) {
      return chartData.__detailRows;
    }

    if (Array.isArray(chartData?.data)) {
      return chartData.data;
    }

    return [];
  }

  protected collectQuadrantDetailRows(source: any): any[] {
    const visited = new Set<any>();
    const queue: any[] = [source];

    while (queue.length) {
      const current = queue.shift();
      if (!current || typeof current !== 'object') {
        continue;
      }

      if (visited.has(current)) {
        continue;
      }
      visited.add(current);

      if (Array.isArray(current)) {
        if (current.some((item) => this.isQuadrantDetailRow(item))) {
          return current;
        }

        queue.push(...current);
        continue;
      }

      queue.push(...Object.values(current));
    }

    return [];
  }

  protected findMatchingDetailRow(point: any, quadrantIndex: number): any {
    if (!Array.isArray(this.currentDetailRows) || this.currentDetailRows.length === 0) {
      return null;
    }

    const quadrantValue = this.resolveQuadrantFilterValue(point?.quadrant, quadrantIndex);
    const normalizedQuadrantValue = this.normalizeCategoryValue(quadrantValue);
    const normalizedLegend = this.normalizeKey(String(point?.legend || ''));
    const normalizedEngineModel = this.normalizeKey(String(point?.engine_model || ''));
    const normalizedCertification = this.normalizeKey(String(point?.certification || ''));
    const normalizedCustomerName = this.normalizeKey(String(point?.customer_name || point?.label || ''));

    const baseCandidates = this.currentDetailRows
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter(({ row, rowIndex }) => {
        if (this.currentMatchedDetailRowIndexes.has(rowIndex)) {
          return false;
        }

        const pointMatchesRow =
          this.isSameNumericPoint(row?.x_axis ?? row?.x, point?.x) &&
          this.isSameNumericPoint(row?.y_axis ?? row?.y, point?.y);

        const engineMatches = normalizedEngineModel
          ? this.normalizeKey(String(row?.engine_model || '')) === normalizedEngineModel
          : true;
        const certificationMatches = normalizedCertification
          ? this.normalizeKey(String(row?.certification || '')) === normalizedCertification
          : true;
        const customerMatches = normalizedCustomerName
          ? this.normalizeKey(String(row?.customer_name || row?.label || '')) === normalizedCustomerName
          : true;
        const legendMatches = normalizedLegend
          ? this.normalizeKey(String(row?.legend || '')) === normalizedLegend
          : true;

        return pointMatchesRow && engineMatches && certificationMatches && customerMatches && legendMatches;
      });

    const candidateIndexes = baseCandidates.filter(({ row }) => {
      if (!normalizedQuadrantValue) {
        return true;
      }

      const rowQuadrantValue = this.normalizeCategoryValue(String(row?.product_price_type || row?.quarter || '').trim());
      return rowQuadrantValue === normalizedQuadrantValue ||
        rowQuadrantValue.includes(normalizedQuadrantValue) ||
        normalizedQuadrantValue.includes(rowQuadrantValue);
    });

    if (candidateIndexes.length > 0) {
      const match = candidateIndexes[0];
      this.currentMatchedDetailRowIndexes.add(match.rowIndex);
      return match.row;
    }

    if (baseCandidates.length > 0) {
      const match = baseCandidates[0];
      this.currentMatchedDetailRowIndexes.add(match.rowIndex);
      return match.row;
    }

    return null;
  }

  protected extractPrimitivePointProps(source: any): Record<string, any> {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return {};
    }

    return Object.entries(source).reduce<Record<string, any>>((acc, [key, value]) => {
      if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  protected isSameNumericPoint(left: any, right: any): boolean {
    const leftValue = Number(left);
    const rightValue = Number(right);
    if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) {
      return false;
    }

    return Math.abs(leftValue - rightValue) < 0.0001;
  }

  protected isQuadrantDetailRow(value: any): boolean {
    return !!(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value.application !== undefined || value.segment !== undefined) &&
      (value.engine_model !== undefined || value.legend !== undefined) &&
      (value.x_axis !== undefined || value.x !== undefined) &&
      (value.y_axis !== undefined || value.y !== undefined)
    );
  }

  protected getQuadrantAreas(focusedQuadrantIndex: number | null = null): any[] {
    const focusedQuadrantCode =
      focusedQuadrantIndex === null || focusedQuadrantIndex < 0
        ? null
        : `Q${focusedQuadrantIndex + 1}`;
    return (this.data.quadrantBackgrounds || [])
      .filter((quad: any) =>
        focusedQuadrantCode === null || this.extractQuadrantCodeFromTitle(quad?.title) === focusedQuadrantCode,
      )
      .map((quad: any) => ([
      {
        name: '',
        xAxis: quad.xAxis,
        yAxis: quad.yAxis,
        itemStyle: { color: quad.color },
      },
      {
        xAxis: quad.x2,
        yAxis: quad.y2,
      },
    ]));
  }

  protected shouldZoomToFocusedQuadrant(): boolean {
    return this.data?.zoomOnQuadrantSelection !== false;
  }

  protected getQuadrantBounds(quadrantIndex: number): { minX: number; maxX: number; minY: number; maxY: number } | null {
    const quads = Array.isArray(this.data?.quadrantBackgrounds) ? this.data.quadrantBackgrounds : [];
    const targetCode = `Q${quadrantIndex + 1}`;
    const quad =
      quads.find((q: any) => this.extractQuadrantCodeFromTitle(q?.title) === targetCode) ??
      quads[quadrantIndex];
    if (!quad) return null;
    return {
      minX: Math.min(Number(quad?.xAxis ?? 0), Number(quad?.x2 ?? 0)),
      maxX: Math.max(Number(quad?.xAxis ?? 0), Number(quad?.x2 ?? 0)),
      minY: Math.min(Number(quad?.yAxis ?? 0), Number(quad?.y2 ?? 0)),
      maxY: Math.max(Number(quad?.yAxis ?? 0), Number(quad?.y2 ?? 0)),
    };
  }

  protected resolveFocusedQuadrantSelection(): {
    quadrant: string;
    quadrantIndex: number;
    productPriceType: string;
    hasExplicitQuadrantSelection: boolean;
  } | null {
    const current = this.globalFilterService.getCurrentFilters();
    const selected = current?.requestParams?.['product_price_type'] ?? current?.queryParams?.['product_price_type'];
    const selectedValue = Array.isArray(selected) ? selected[0] : selected;
    const normalizedSelected = this.normalizeCategoryValue(selectedValue);
    const selectedQuadrantIndexRaw =
      current?.requestParams?.['selected_quadrant_index'] ??
      current?.queryParams?.['selected_quadrant_index'];
    const selectedQuadrantRaw =
      current?.requestParams?.['selected_quadrant'] ??
      current?.queryParams?.['selected_quadrant'];

    const selectedQuadrantIndex = Number(
      Array.isArray(selectedQuadrantIndexRaw) ? selectedQuadrantIndexRaw[0] : selectedQuadrantIndexRaw,
    );
    const selectedQuadrant = String(Array.isArray(selectedQuadrantRaw) ? selectedQuadrantRaw[0] : selectedQuadrantRaw || '').trim();

    if (!normalizedSelected && !selectedQuadrant) return null;

    if (Number.isFinite(selectedQuadrantIndex) && selectedQuadrantIndex >= 0) {
      const q = selectedQuadrant || `Q${selectedQuadrantIndex + 1}`;
      return {
        quadrant: q,
        quadrantIndex: selectedQuadrantIndex,
        productPriceType: String(selectedValue || this.resolveQuadrantFilterValue(q, selectedQuadrantIndex)),
        hasExplicitQuadrantSelection: true,
      };
    }

    if (selectedQuadrant) {
      const match = selectedQuadrant.match(/\bQ([1-4])\b/i);
      if (match) {
        const idx = Number(match[1]) - 1;
        return {
          quadrant: `Q${match[1]}`,
          quadrantIndex: idx,
          productPriceType: String(selectedValue || this.resolveQuadrantFilterValue(`Q${match[1]}`, idx)),
          hasExplicitQuadrantSelection: true,
        };
      }
    }

    const map = this.data?.quadrantValueMap || {};
    const entries = Object.entries(map);
    for (const [quadrant, mapped] of entries) {
      if (this.normalizeCategoryValue(mapped) === normalizedSelected) {
        const idx = Number(String(quadrant).replace(/[^\d]/g, '')) - 1;
        if (Number.isFinite(idx) && idx >= 0) {
          return {
            quadrant: String(quadrant),
            quadrantIndex: idx,
            productPriceType: String(mapped),
            hasExplicitQuadrantSelection: false,
          };
        }
      }
    }

    return null;
  }

  protected normalizeCategoryValue(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  protected extractQuadrantCodeFromTitle(title: any): string {
    const text = String(title || '').trim();
    const match = text.match(/\bQ([1-4])\b/i);
    return match ? `Q${match[1]}` : '';
  }

  protected resolveChartData(source: any): any {
    const seriesPath = String(this.data?.series || '').trim();
    if (seriesPath) {
      const resolved = this.resolvePath(source, seriesPath);
      if (resolved !== undefined && resolved !== null) {
        if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
          const detailRows = this.collectQuadrantDetailRows(source);
          return {
            ...resolved,
            __detailRows: detailRows,
          };
        }
        return resolved;
      }
    }

    return source?.chartData ?? source?.data?.chartData ?? source?.data ?? source;
  }

  protected resolvePath(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  protected buildTooltipContext(params: any): any {
    const point = params?.data;
    if (point && typeof point === 'object' && !Array.isArray(point)) {
      return { ...params, ...point };
    }
    return { ...params, data: point };
  }

  protected interpolateTooltipTemplate(template: string, data: any): string {
    return String(template).replace(/\{([^}]+)\}/g, (_match: string, rawToken: string) => {
      const token = String(rawToken || '').trim();
      if (!token) return '-';
      if (token === 'b') return String(data?.name ?? '-');
      if (token === 'x') return this.formatCoordinateTooltipValue(data?.x ?? data?.value?.[0]);
      if (token === 'y') return this.formatCoordinateTooltipValue(data?.y ?? data?.value?.[1]);

      const value = this.resolveTooltipValue(data, token);
      return value === undefined || value === null ? '-' : String(value);
    });
  }

  protected formatCoordinateTooltipValue(value: any): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '-';
    }

    return String(Math.trunc(numericValue));
  }

  protected resolveTooltipAxisName(name: any, fallback: string): string {
    const resolvedName = String(name ?? '').trim();
    return resolvedName || fallback;
  }

  protected resolveTooltipValue(data: any, key: string): any {
    const token = String(key || '').trim();
    if (!token) return undefined;

    const idx = Number(token);
    if (!Number.isNaN(idx) && Array.isArray(data?.value)) {
      const fromValue = data.value[idx];
      if (fromValue !== undefined) return fromValue;
    }

    const direct = data?.[token];
    if (direct !== undefined) return direct;

    const nested = this.resolvePath(data, token);
    if (nested !== undefined) return nested;

    const normalizedToken = this.normalizeKey(token);
    for (const currentKey of Object.keys(data || {})) {
      if (this.normalizeKey(currentKey) === normalizedToken) {
        return data[currentKey];
      }
    }

    const deepValue = this.findValueByNormalizedKey(data, normalizedToken);
    if (deepValue !== undefined) {
      return deepValue;
    }

    return undefined;
  }

  protected normalizeKey(value: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  protected findValueByNormalizedKey(source: any, normalizedKey: string): any {
    const visited = new Set<any>();
    const queue: any[] = [source];

    while (queue.length) {
      const current = queue.shift();
      if (!current || typeof current !== 'object') continue;
      if (visited.has(current)) continue;
      visited.add(current);

      if (Array.isArray(current)) {
        queue.push(...current);
        continue;
      }

      for (const currentKey of Object.keys(current)) {
        if (this.normalizeKey(currentKey) === normalizedKey) {
          return current[currentKey];
        }
      }

      queue.push(...Object.values(current));
    }

    return undefined;
  }

  protected setupBubbleClickEvent(): void {
    if (!this.chart) return;

    this.chart.off('click');
    if (this.data?.disableBubbleClick === true) {
      return;
    }
    this.chart.on('click', (params: any) => {
      if (params?.componentType !== 'series') return;
      this.handleBubblePointInteraction(params?.data || {}, params);
    });
  }

  protected setupQuadrantAreaClickEvent(): void {
    if (!this.chart) return;
    const zr = this.chart.getZr();
    zr.off('click');
    zr.on('click', (evt: any) => {
      const px = [evt?.offsetX, evt?.offsetY];
      if (!this.chart.containPixel('grid', px)) return;

      if (this.data?.disableQuadrantAreaClick === true && this.data?.disableBubbleClick !== true && evt?.target) {
        const point = this.resolveBubblePointFromPixel(px);
        if (point) {
          this.handleBubblePointInteraction(point, {
            componentType: 'series',
            data: point,
            event: evt,
          });
        }
        return;
      }

      if (!this.shouldEnableQuadrantAreaClick()) {
        return;
      }

      // If user clicked an actual element (bubble), let series click handler manage it.
      if (evt?.target) return;

      const value = this.chart.convertFromPixel({ xAxisIndex: 0, yAxisIndex: 0 }, px) as number[];
      if (!Array.isArray(value) || value.length < 2) return;
      const [x, y] = value.map((v) => Number(v));
      const quads = Array.isArray(this.data?.quadrantBackgrounds) ? this.data.quadrantBackgrounds : [];
      const idx = quads.findIndex((q: any) => {
        const minX = Math.min(Number(q?.xAxis ?? 0), Number(q?.x2 ?? 0));
        const maxX = Math.max(Number(q?.xAxis ?? 0), Number(q?.x2 ?? 0));
        const minY = Math.min(Number(q?.yAxis ?? 0), Number(q?.y2 ?? 0));
        const maxY = Math.max(Number(q?.yAxis ?? 0), Number(q?.y2 ?? 0));
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
      });
      if (idx < 0) return;

      const clickedQuad = quads[idx] || {};
      const title = String(clickedQuad?.title || '').trim();
      const matchedQuadrant = title.match(/\bQ([1-4])\b/i);
      const quadrant = matchedQuadrant ? `Q${matchedQuadrant[1]}` : `Q${idx + 1}`;
      const quadrantIndex = Number(quadrant.replace('Q', '')) - 1;
      const filterKey = String(this.data?.filterKey || 'product_price_type').trim();
      const mappedFilterValue = this.resolveQuadrantFilterValue(quadrant, quadrantIndex);
      const payload: any = {
        [filterKey]: mappedFilterValue,
        product_price_type: mappedFilterValue,
        quadrant,
        quadrantIndex,
        x,
        y,
      };

      this.chartFilterHandler.updateFilterSelection(this.data, payload, payload);
      if (this.data?.navigateAction) {
        const filters = this.globalFilterService.getCurrentFilters();
        const route = String(this.data?.navigateAction?.route || 'dvm/deepdive').replace(/^\//, '');
        this.router.navigate([route], {
          state: {
            crossNavPayload: {
              requestData: {
                ...(filters?.requestParams || {}),
                product_price_type: mappedFilterValue,
                price_increase: null,
                selected_quadrant: quadrant,
                selected_quadrant_index: quadrantIndex,
              },
            },
          },
        });
      }
    });
  }

  protected shouldEnableQuadrantAreaClick(): boolean {
    if (this.data?.disableQuadrantAreaClick === true) {
      return false;
    }

    if (this.data?.enableQuadrantAreaClick === false) {
      return false;
    }

    return true;
  }

  protected handleBubblePointInteraction(point: any, params?: any): void {
    if (!point) return;
    const now = Date.now();
    if (now - this.lastBubbleInteractionAt < 250) {
      return;
    }
    this.lastBubbleInteractionAt = now;

    const raw = Array.isArray(point?.value) ? point.value : point?.value ?? point;
    const quadrantIndex = Number(point?.quadrantIndex ?? raw?.[2]);
    const quadrant = String(point?.quadrant ?? (Number.isFinite(quadrantIndex) ? `Q${quadrantIndex + 1}` : ''));
    const filterKey = String(this.data?.filterKey || 'quadrant').trim();
    if (!filterKey || !quadrant) return;

    const mappedFilterValue = String(
      point?.product_price_type ??
      this.resolveQuadrantFilterValue(quadrant, quadrantIndex),
    );

    const payload = {
      [filterKey]: mappedFilterValue,
      product_price_type: mappedFilterValue,
      key: point?.key ?? point?.name,
      label: point?.label ?? point?.name,
      x: point?.x ?? raw?.[0],
      y: point?.y ?? raw?.[1],
      quadrant,
      quadrantIndex,
    };

    const eventContext = {
      ...point,
      ...payload,
      cluster: quadrantIndex,
    };

    this.chartFilterHandler.updateFilterSelection(this.data, payload, eventContext);

    if (!this.data?.navigateAction) {
      return;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const handled = executeNavigationFromConfig(this.router, this.data, {
      ...(params || {}),
      componentType: params?.componentType || 'series',
      data: point,
      name: point?.name ?? point?.label,
      value: point?.value,
      seriesName: point?.legend,
    }, {
      filters,
      requestParams: {
        ...(filters?.requestParams || {}),
        ...payload,
        uid: point?.uid ?? null,
        price_increase: point?.price_increase ?? null,
        selected_quadrant: quadrant,
        selected_quadrant_index: quadrantIndex,
      },
      row: point,
      point,
      event: eventContext,
    });

    if (!handled) {
      const route = String(this.data?.navigateAction?.route || 'dvm/deepdive').replace(/^\//, '');
      this.router.navigate([route], {
        state: {
          crossNavPayload: {
            requestData: {
              ...(filters?.requestParams || {}),
              product_price_type: mappedFilterValue,
              uid: point?.uid ?? null,
              price_increase: point?.price_increase ?? null,
              selected_quadrant: quadrant,
              selected_quadrant_index: quadrantIndex,
            },
          },
        },
      });
    }
  }

  protected resolveBubblePointFromPixel(pixel: number[]): any | null {
    if (!this.chart || !Array.isArray(this.renderedPoints) || !this.renderedPoints.length) {
      return null;
    }

    const fallbackSize = Number(this.data?.symbolSize) || 15;
    let bestMatch: { point: any; distance: number } | null = null;

    for (const point of this.renderedPoints) {
      const x = Number(point?.x ?? point?.value?.[0]);
      const y = Number(point?.y ?? point?.value?.[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

      const pointPixel = this.chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [x, y]) as number[];
      if (!Array.isArray(pointPixel) || pointPixel.length < 2) continue;

      const dx = Number(pointPixel[0]) - Number(pixel[0]);
      const dy = Number(pointPixel[1]) - Number(pixel[1]);
      const distance = Math.sqrt((dx * dx) + (dy * dy));
      const visibleSize = this.resolveVisibleBubbleSize(point?.size ?? point?.value?.[3], fallbackSize);
      const radius = Math.max(visibleSize / 2, 10);

      if (distance <= radius && (!bestMatch || distance < bestMatch.distance)) {
        bestMatch = { point, distance };
      }
    }

    return bestMatch?.point || null;
  }

  protected resizeChart = () => {
    this.chart?.resize();
  };

  protected resolveQuadrantFilterValue(quadrant: string, quadrantIndex: number): string {
    const map = this.data?.quadrantValueMap || {};
    const direct = map?.[quadrant];
    if (direct) return String(direct);

    const bg = Array.isArray(this.data?.quadrantBackgrounds)
      ? this.data.quadrantBackgrounds[quadrantIndex]
      : null;
    const title = String(bg?.title || '').trim();
    if (title.includes('-')) {
      return title.split('-').slice(1).join('-').trim();
    }

    return quadrant;
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.dataSub?.unsubscribe();
    this.wrapper.detach();
    window.removeEventListener('resize', this.resizeChart);
    this.chart?.dispose();
  }
}
